<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <v-expansion-panels v-model="expandOptions">
          <v-expansion-panel>
            <v-expansion-panel-header>Report Filters</v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-form>
                <v-select
                  :items="trainingUnits"
                  v-model="filterUnitCode"
                  label="Filter Training Unit"
                />
                <v-select
                  :items="traineeStatusItems"
                  v-model="traineeStatus"
                  label="Trainee Status"
                />
                <v-checkbox
                  v-model="includeFailed"
                  label="Include failed assessments"
                />
                <v-checkbox
                  v-model="includeEmpty"
                  label="Include people without training"
                />
                <v-row>
                  <v-col cols="12" md="6">
                    <date-input
                      label="From"
                      v-model="minDateValue"
                      :max="maxDateValue"
                      required
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <date-input
                      label="To"
                      v-model="maxDateValue"
                      :min="minDateValue"
                      required
                    />
                  </v-col>
                </v-row>
              </v-form>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <report
          :name="`msa_trained_by_trainee_${memberNumber}`"
          :disableInput="$apollo.queries.trainees.loading"
          :loading="$apollo.queries.trainees.loading"
        >
          <v-row>
            <v-col cols="12">
              <h5 class="text-h5 text-center">
                Member Trainees Report
              </h5>
            </v-col>

            <v-col cols="12">
              <p class="font-weight-bold font-italic">
                Your selection criteria...
              </p>
              <v-simple-table
                dense
              >
                <template v-slot:default>
                  <tbody>
                    <tr>
                      <th>Member:</th>
                      <td>{{ memberNumber }} - {{ memberDetails.memberName }}</td>
                      <th>Training Unit:</th>
                      <td>{{ filterUnitCode || '*' }}</td>
                    </tr>
                    <tr>
                      <th>Dates:</th>
                      <td>
                        <span v-show="!!minDate">{{ minDate | formatDate }}</span>
                        <span v-show="!minDate">*</span>
                      </td>
                      <th>To:</th>
                      <td>
                        <span v-show="!!maxDate">{{ maxDate | formatDate }}</span>
                        <span v-show="!maxDate">*</span>
                      </td>
                    </tr>
                    <tr>
                      <th>Include failed assessments:</th>
                      <td>{{ includeFailed | yesNo }}</td>
                      <th>Include people without training:</th>
                      <td>{{ includeEmpty | yesNo }}</td>
                    </tr>
                    <tr>
                      <th>Trainee Status:</th>
                      <td>
                        <template v-if="traineeStatus === true">Current</template>
                        <template v-else-if="traineeStatus === false">Inactive</template>
                        <template v-else>*</template>
                      </td>
                      <th></th>
                      <td></td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-col>
          </v-row>

          <v-row v-show="!sortedItems.length">
            <v-col>
              <p class="font-weight-bold font-italic">
                There are no records to show.
              </p>
            </v-col>
          </v-row>
          <v-row
            v-for="item of sortedItems"
            :key="item._id"
            style="margin-top: 20px;"
          >
            <v-col>
              <p class="font-weight-bold font-italic">
                {{ item.name }} - MSA ID: {{ item.personId }}
              </p>

              <v-simple-table
                dense
              >
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th>
                        MSA Member
                      </th>
                      <th>
                        Training Module
                      </th>
                      <th>
                        Training Unit
                      </th>
                      <th class="text-right">
                        Date
                      </th>
                      <th class="text-right">
                        Passed
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="assessment of item.assessments"
                      :key="assessment._id"
                    >
                      <td>{{ memberNumber }}</td>
                      <td>{{ assessment.moduleCode }} &dash; {{ assessment.moduleDescription }}</td>
                      <td>{{ assessment.unitCode }} &dash; {{ assessment.unitDescription }}</td>
                      <td class="text-right">{{ assessment.completionDate | formatDate }}</td>
                      <td class="text-right">{{ assessment.passed | yesNo }}</td>
                    </tr>
                    <tr>
                      <td class="text-right font-weight-medium" colspan="4">Units passed</td>
                      <td class="text-right font-weight-medium">{{ item.assessmentsPassedCount }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-col>
          </v-row>
        </report>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import gql from 'graphql-tag'
import { orderBy } from 'lodash'
import { isAfter, isBefore, endOfDay, startOfDay, subYears } from 'date-fns'

import { sortDatesDesc } from '../../utilities/index.js'
import Report from '../../components/Report.vue'
import DateInput from '../../components/inputs/DateInput.vue'

export default {
  name: 'MemeberTraineesReportView',

  components: {
    Report,
    DateInput
  },

  props: {
    memberNumber: {
      type: String
    },
    trainerId: {
      type: String
    }
  },

  data () {
    const now = new Date()
    return {
      includeFailed: false,
      filterUnitCode: 'MOD-EUT-01',
      includeEmpty: false,
      includeRegistrationPersons: true,
      minDateValue: subYears(now, 1).toISOString(),
      maxDateValue: now.toISOString(),
      trainees: [],
      expandOptions: 0,
      trainingModules: [],
      traineeStatusItems: [
        { text: 'Any', value: null },
        { text: 'Current', value: true },
        { text: 'Inactive', value: false }
      ],
      traineeStatus: true,
      memberDetails: {}
    }
  },

  computed: {
    minDate () {
      return this.minDateValue ? new Date(this.minDateValue) : null
    },

    maxDate () {
      return this.maxDateValue ? new Date(this.maxDateValue) : null
    },

    items () {
      return this.trainees
        .map(rp => {
          const assessments = rp.trainingAssessments
            .filter(assessment => this.includeFailed ? true : assessment.passed) // Only passed
            .filter(assessment => this.filterUnitCode ? assessment.unitCode === this.filterUnitCode : true)
            .map(assessment => ({
              _id: assessment._id,
              passed: assessment.passed,
              unitCode: assessment.unitCode,
              unitDescription: assessment.session?.module?.units.find(o => o.code === assessment.unitCode)?.description,
              moduleCode: assessment.session?.module?.code ?? 'Unknown',
              moduleDescription: assessment.session?.module?.description ?? 'Unknown',
              completionDate: assessment.session?.completionDate
            }))
            .filter(assessment => this.minDate ? isAfter(new Date(assessment.completionDate), startOfDay(this.minDate)) : true)
            .filter(assessment => this.maxDate ? isBefore(new Date(assessment.completionDate), endOfDay(this.maxDate)) : true)
            .sort((a, b) => sortDatesDesc(a.completionDate, b.completionDate))
          return {
            _id: rp._id,
            personId: rp.personId,
            name: rp.name,
            givenName: rp.givenName,
            familyName: rp.familyName,
            assessmentsPassedCount: assessments.filter(o => o.passed).length,
            assessments
          }
        })
    },

    filteredItems () {
      return this.items
        .filter(item => this.includeEmpty ? true : item.assessments.length > 0)
    },

    sortedItems () {
      return orderBy(this.filteredItems, ['givenName', 'familyName'])
    },

    trainingUnits () {
      const units = this.trainingModules
        .flatMap(o => o.units)
        .map(o => ({
          text: `${o.code} - ${o.description}`,
          value: o.code
        }))
      return [
        {
          text: 'All',
          value: null
        },
        ...orderBy(units, ['text'])
      ]
    }
  },

  apollo: {
    trainees: {
      query: gql`query GetMemberTraining($memberNumber: String!, $includeRegistrationPersons: Boolean!, $isActive: Boolean, $minDate: Date, $maxDate: Date) {
        trainees: memberTraineeMany(
          memberNumber: $memberNumber,
          includeRegistrationPersons: $includeRegistrationPersons,
          isActive: $isActive,
          minDate: $minDate,
          maxDate: $maxDate
        ) {
          _id
          personId
          name
          givenName
          familyName
          trainingAssessments {
            _id
            unitCode
            assessment
            passed
            session {
              _id
              completionDate
              module {
                _id
                code
                description
                units {
                  _id
                  code
                  description
                }
              }
            }
          }
        }
      }`,
      variables () {
        return {
          memberNumber: this.memberNumber,
          includeRegistrationPersons: this.includeRegistrationPersons,
          isActive: this.traineeStatus,
          minDate: this.minDate ? startOfDay(this.minDate) : null,
          maxDate: this.maxDate ? endOfDay(this.maxDate) : null
        }
      },
      error (e) { this.$emit('error', e) },
      skip () {
        return !this.memberNumber
      }
    },

    trainingModules: {
      query: gql`query GetMemberTrainingModules {
        trainingModules: publicTrainingModuleMany(sort: CODE_ASC) {
          _id
          code
          description
          active
          units {
            _id
            code
            description
            active
          }
        }
      }`,
      error (e) { this.$emit('error', e) }
    },

    memberDetails: {
      query: gql`query GetMemberDetailsForTraineesReport($memberNumber: String!) {
        memberDetails(memberNumber: $memberNumber) {
          _id
          memberName
        }
      }`,
      variables () {
        return {
          memberNumber: this.memberNumber
        }
      },
      error (e) { this.$emit('error', e) },
      skip () {
        return !this.memberNumber
      }
    }
  }
}
</script>
